import { Box, List, ListItem } from '@mui/material';
import { Engine } from 'excalibur';
import { useEffect, useRef, useState } from 'react';

export const Menu = <T extends string>({
    title,
    options,
    select,
    engine,
}: {
    title?: string;
    options: Array<[text: string, value: T]>;
    select: (option: T) => void;
    engine: Engine;
}) => {
    const oneOption = options.length === 1;

    const [selectedIndex, setSelectedIndex] = useState(0);
    const selectedIndexRef = useRef(0);
    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    useEffect(() => {
        setSelectedIndex((i) => Math.max(0, Math.min(options.length - 1, i)));
    }, [options.length]);

    // Edge-trigger for left stick Y
    const lastYDirRef = useRef<-1 | 0 | 1>(0);
    const stickThreshold = 0.55;

    useEffect(() => {
        if (oneOption) {
            const only = options[0]![1];
            const onButton = () => select(only);

            engine.input.gamepads.on('button', onButton);
            return () => engine.input.gamepads.off('button', onButton);
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.repeat) return;

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => (i - 1 + options.length) % options.length);
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => (i + 1) % options.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const v = options[selectedIndexRef.current]?.[1];
                if (v) select(v);
            }
        };

        window.addEventListener('keydown', onKeyDown, { capture: true });

        const onPostUpdate = () => {
            for (let p = 0; p < 4; p++) {
                const pad = engine.input.gamepads.at(p);
                if (!pad) continue;

                const dpadUp = pad.wasButtonPressed(12 as any);
                const dpadDown = pad.wasButtonPressed(13 as any);
                const confirm = pad.wasButtonPressed(0 as any);

                if (dpadUp) {
                    setSelectedIndex((i) => (i - 1 + options.length) % options.length);
                    return;
                }
                if (dpadDown) {
                    setSelectedIndex((i) => (i + 1) % options.length);
                    return;
                }

                const y = pad.getAxes(1);
                const dir: -1 | 0 | 1 = y <= -stickThreshold ? -1 : y >= stickThreshold ? 1 : 0;

                if (dir !== 0 && lastYDirRef.current === 0) {
                    if (dir === -1) setSelectedIndex((i) => (i - 1 + options.length) % options.length);
                    if (dir === 1) setSelectedIndex((i) => (i + 1) % options.length);
                    lastYDirRef.current = dir;
                    return;
                }
                if (dir === 0) lastYDirRef.current = 0;

                if (confirm) {
                    const v = options[selectedIndexRef.current]?.[1];
                    if (v) select(v);
                    return;
                }
            }
        };

        engine.on('postupdate', onPostUpdate);
        return () => {
            window.removeEventListener('keydown', onKeyDown, { capture: true } as any);
            engine.off('postupdate', onPostUpdate);
        };
    }, [engine, oneOption, options, select]);

    const LEGO = {
        red: '#D50000',
        blue: '#0B57D0',
        yellow: '#FFD200',
        black: '#111111',
        white: '#FFFFFF',
        shadow: 'rgba(17,17,17,0.22)',
    } as const;

    const itemColors = [LEGO.red, LEGO.blue, LEGO.yellow] as const;

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                display: 'grid',
                // Top-half placement: centered horizontally, but aligned toward the top
                placeItems: 'start center',
                pt: 6,
                px: 2,
                userSelect: 'none',
                background: `
          radial-gradient(900px 600px at 50% 35%, rgba(255,255,255,0.75), rgba(255,255,255,0.25)),
          linear-gradient(135deg, rgba(213,0,0,0.18), rgba(11,87,208,0.16) 45%, rgba(255,210,0,0.20)),
          radial-gradient(circle at 1px 1px, rgba(17,17,17,0.07) 1px, transparent 0)
        `,
                backgroundSize: 'auto, auto, 10px 10px',
            }}
        >
            <Box
                sx={{
                    width: 'min(600px, 92vw)',
                    // Only as tall as needed; avoid forcing a fill
                    height: 'auto',
                    maxHeight: '50vh', // keep it within top half even with lots of options
                    overflow: 'hidden',
                    borderRadius: 4,
                    backgroundColor: LEGO.white,
                    border: `2px solid ${LEGO.black}`,
                    boxShadow: `0 28px 70px ${LEGO.shadow}, 0 10px 26px rgba(0,0,0,0.18)`,
                    transform: 'translateZ(0)',
                }}
            >
                {/* header */}
                <Box
                    sx={{
                        p: 2,
                        color: LEGO.black,
                        background: `linear-gradient(90deg, ${LEGO.red}, ${LEGO.blue} 55%, ${LEGO.yellow})`,
                        borderBottom: `2px solid ${LEGO.black}`,
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 8,
                            height: 18,
                            opacity: 0.22,
                            background: `radial-gradient(circle, rgba(255,255,255,0.95) 0 7px, transparent 8px)`,
                            backgroundSize: '22px 18px',
                            backgroundPosition: '12px 0px',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    {title && (
                        <Box
                            sx={{
                                display: 'inline-block',
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 2,
                                backgroundColor: 'rgba(255,255,255,0.88)',
                                border: `2px solid ${LEGO.black}`,
                                boxShadow: `0 6px 0 rgba(0,0,0,0.18)`,
                                fontSize: 20,
                                fontWeight: 900,
                                letterSpacing: '0.03em',
                                lineHeight: 1.15,
                            }}
                        >
                            {title}
                        </Box>
                    )}
                </Box>

                {/* options */}
                <Box sx={{ p: 2 }}>
                    {/* If the list gets too long, scroll only the list area, not the whole panel */}
                    <List sx={{ p: 0, m: 0, maxHeight: 'calc(50vh - 140px)', overflowY: 'auto' }}>
                        {options.map(([text, value], idx) => {
                            const c = itemColors[idx % itemColors.length];
                            const isSelected = !oneOption && idx === selectedIndex;

                            return (
                                <ListItem
                                    key={text}
                                    onMouseEnter={() => setSelectedIndex(idx)}
                                    onPointerEnter={() => setSelectedIndex(idx)}
                                    onClick={() => select(value)}
                                    tabIndex={0}
                                    role="button"
                                    sx={{
                                        width: '96%',
                                        mx: 'auto',
                                        my: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        border: `2px solid ${LEGO.black}`,
                                        backgroundColor: c,
                                        color: LEGO.white,
                                        boxShadow: isSelected
                                            ? `0 0 0 4px rgba(255,255,255,0.85), 0 0 0 7px ${LEGO.black}, 0 10px 0 rgba(0,0,0,0.26)`
                                            : `0 8px 0 rgba(0,0,0,0.28)`,
                                        filter: isSelected ? 'brightness(1.08)' : 'none',
                                        transform: isSelected ? 'translateY(-1px)' : 'none',
                                        transition: 'transform 80ms ease, box-shadow 120ms ease, filter 120ms ease',
                                        '&::before': oneOption
                                            ? {}
                                            : {
                                                  content: isSelected ? '"▶"' : '" "',
                                                  display: 'inline-grid',
                                                  placeItems: 'center',
                                                  width: 28,
                                                  height: 28,
                                                  borderRadius: 999,
                                                  backgroundColor: 'rgba(255,255,255,0.90)',
                                                  color: LEGO.black,
                                                  border: `2px solid ${LEGO.black}`,
                                                  boxShadow: `0 2px 0 rgba(0,0,0,0.22)`,
                                                  transform: 'translateY(-1px)',
                                                  flex: '0 0 auto',
                                              },
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            filter: 'brightness(1.05)',
                                            boxShadow: isSelected
                                                ? `0 0 0 4px rgba(255,255,255,0.85), 0 0 0 7px ${LEGO.black}, 0 10px 0 rgba(0,0,0,0.26)`
                                                : `0 10px 0 rgba(0,0,0,0.26)`,
                                        },
                                        '&:active': {
                                            transform: 'translateY(6px)',
                                            boxShadow: `0 2px 0 rgba(0,0,0,0.26)`,
                                        },
                                        '&:focus-visible': {
                                            outline: 'none',
                                            boxShadow: `0 0 0 4px rgba(255,255,255,0.85), 0 0 0 7px ${LEGO.black}, 0 10px 0 rgba(0,0,0,0.26)`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            fontWeight: 900,
                                            letterSpacing: '0.03em',
                                            textTransform: 'uppercase',
                                            fontSize: 14,
                                            textShadow: '0 2px 0 rgba(0,0,0,0.25)',
                                        }}
                                    >
                                        {text}
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>

                    <Box
                        sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: `2px solid ${LEGO.black}`,
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            color: 'rgba(17,17,17,0.75)',
                            fontSize: 11,
                            letterSpacing: '0.10em',
                            textTransform: 'uppercase',
                        }}
                    >
                        <Box>Mouse / touch / gamepad</Box>
                        {!oneOption && <Box>D-pad / stick / hover · A / Enter to select</Box>}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
